import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Trash2 } from 'lucide-react';
import { useListingActions } from '../../../hooks/useListingAdmin';
import { useListing, useListings } from '../../../hooks/useListings';
import { LendingType, Listing, Nusach, RavEntry } from '../../../types';
import { BEIS_HORAAH_TOPICS, LANGUAGE_CODES } from '../../../config/categories';
import { cleanForSave, LISTING_LIMITS } from '../../../utils/listingEdit';
import { pickLang } from '../../../utils/listingDisplay';
import {
  BilingualField,
  Checkbox,
  ChipSelect,
  SaveBar,
  SectionTitle,
  SelectField,
  TextField,
  useFormDraft,
} from './formFields';
import type { TabProps } from './ListingEditor';
import ShiurDetailsFields from './ShiurDetailsFields';
import { DEFAULT_AREA_ID } from '../../../config/areas';

const NUSACH_OPTIONS: Nusach[] = ['ashkenaz', 'sfarad', 'edotMizrach', 'yemenite', 'other'];
const LENDING_TYPES: LendingType[] = ['loan', 'giveaway', 'rental', 'mixed'];

type Draft = Record<string, unknown>;

/** Numbers are typed as text in the form and stored as numbers. */
function toNumberOrUndefined(value: unknown): number | undefined {
  if (value === '' || value === undefined || value === null) return undefined;
  const n = Number(value);
  return Number.isFinite(n) && n >= 0 ? Math.round(n) : undefined;
}

export default function DetailsTab({ listing }: TabProps) {
  const { t } = useTranslation();
  const { updateListing, saving } = useListingActions();
  const source: Draft =
    listing.category === 'shul' ? { nusach: listing.nusach ?? 'ashkenaz' } : { ...(listing.details as Draft) };
  const { draft, update, dirty, reset, save, justSaved, failed } = useFormDraft<Draft>(source);
  const [shulError, setShulError] = useState(false);
  // A shiur in a shul takes the shul's address and pin (copied on save, so map and search work as for any listing).
  const linkedShulId = listing.category === 'shiur' && draft.where === 'shul' ? ((draft.shulId as string) ?? '') : '';
  const { listing: linkedShul } = useListing(linkedShulId);

  const str = (key: string) => (draft[key] as string | undefined) ?? '';
  const bool = (key: string) => draft[key] === true;
  const f = (key: string) => t(`directory.field.${key}`);
  const bilingual = (base: string, labelKey: string, multiline = false) => (
    <BilingualField
      labelHe={`${f(labelKey)} (${t('directory.admin.hebrew')})`}
      labelEn={`${f(labelKey)} (${t('directory.admin.english')})`}
      valueHe={str(`${base}He`)}
      valueEn={str(`${base}En`)}
      onChangeHe={(v) => update({ [`${base}He`]: v })}
      onChangeEn={(v) => update({ [`${base}En`]: v })}
      multiline={multiline}
      maxLength={multiline ? LISTING_LIMITS.textLength : LISTING_LIMITS.noteLength}
    />
  );
  const check = (key: string, labelKey = key) => (
    <Checkbox label={f(labelKey)} checked={bool(key)} onChange={(v) => update({ [key]: v })} />
  );

  const handleSave = async () => {
    if (listing.category === 'shul') {
      await save(() => updateListing(listing.id, { nusach: draft.nusach }));
      return;
    }
    const details: Draft = { ...draft };
    if (listing.category === 'mikvah') details.mikvahType = listing.subcategory;
    if (listing.category === 'service') {
      details.capacity = toNumberOrUndefined(details.capacity);
      details.beds = toNumberOrUndefined(details.beds);
      const prev = listing.details;
      if (
        (details.statusMessageHe ?? '') !== (prev.statusMessageHe ?? '') ||
        (details.statusMessageEn ?? '') !== (prev.statusMessageEn ?? '')
      ) {
        details.statusUpdatedAt = new Date().toISOString();
      }
    }
    if (listing.category === 'beisHoraah') {
      details.rabbanim = ((details.rabbanim as RavEntry[] | undefined) ?? []).filter(
        (r) => r.nameHe?.trim() || r.nameEn?.trim()
      );
    }
    for (const [key, value] of Object.entries(details)) {
      if (typeof value === 'string') details[key] = value.trim();
    }
    if (listing.category === 'shiur') {
      const where = (details.where as string) || 'place';
      setShulError(where === 'shul' && !details.shulId);
      if (where === 'shul' && (!details.shulId || !linkedShul)) return;
      details.where = where;
      if (where !== 'shul') {
        delete details.shulId;
        delete details.roomHe;
        delete details.roomEn;
      }
      details.audience = details.audience ?? [];
      details.languages = details.languages ?? [];
      // Times are edited on their own tab: keep what is saved now.
      details.sessions = listing.details?.sessions ?? [];
      const patch: Record<string, unknown> = { details: cleanForSave(details) };
      if (where === 'shul' && linkedShul) patch.location = { ...linkedShul.location };
      if (where === 'online') {
        patch.location = { areaId: listing.location?.areaId ?? DEFAULT_AREA_ID, addressHe: '', addressEn: '', showExactLocation: true };
      }
      await save(() => updateListing(listing.id, patch));
      return;
    }
    await save(() => updateListing(listing.id, { details: cleanForSave(details) }));
  };

  let fields: React.ReactNode;
  switch (listing.category) {
    case 'shul':
      fields = (
        <SelectField
          label={t('admin.nusach')}
          info={t('info.nusach')}
          value={str('nusach') as Nusach}
          onChange={(v) => update({ nusach: v })}
          options={NUSACH_OPTIONS.map((n) => ({ value: n, label: t(`nusach.${n}`) }))}
        />
      );
      break;

    case 'gemach':
      fields = (
        <>
          {bilingual('itemsDescription', 'itemsDescription', true)}
          <SelectField
            label={f('lendingType')}
            value={str('lendingType') as LendingType | ''}
            onChange={(v) => update({ lendingType: v || undefined })}
            options={[
              { value: '', label: t('directory.admin.notSet') },
              ...LENDING_TYPES.map((l) => ({ value: l, label: t(`directory.lendingType.${l}`) })),
            ]}
          />
          {bilingual('feeNote', 'feeNote')}
          {check('depositRequired')}
          {check('pickupOnly')}
          {check('donationsAccepted')}
        </>
      );
      break;

    case 'mikvah':
      fields = (
        <>
          <p className="text-sm text-ash">
            {f('mikvahType')}: {t(`directory.sub.${listing.subcategory}`)} · {t('directory.admin.details.mikvahTypeHint')}
          </p>
          {check('appointmentRequired')}
          {check('accessible')}
          {bilingual('feeNote', 'feeNote')}
          {bilingual('supervision', 'supervision')}
        </>
      );
      break;

    case 'beisHoraah':
      fields = (
        <BeisHoraahFields
          listing={listing}
          draft={draft}
          update={update}
          check={check}
        />
      );
      break;

    case 'organization':
      fields = (
        <>
          {bilingual('services', 'services', true)}
          {listing.subcategory === 'emergency' &&
            <Checkbox
              label={f('emergency')}
              hint={t('directory.admin.details.emergencyHint')}
              checked={bool('emergency')}
              onChange={(v) => update({ emergency: v })}
            />}
          {check('available247')}
          <TextField
            label={f('donationLink')}
            type="url"
            dir="ltr"
            value={str('donationLink')}
            onChange={(v) => update({ donationLink: v })}
            placeholder="https://"
          />
        </>
      );
      break;

    case 'service':
      fields = (
        <>
          {listing.subcategory === 'simchaHalls' && (
            <TextField
              label={f('capacity')}
              type="number"
              inputMode="numeric"
              dir="ltr"
              value={String(draft.capacity ?? '')}
              onChange={(v) => update({ capacity: v })}
            />
          )}
          {listing.subcategory === 'shabbosApartments' && (
            <>
              {check('free')}
              <TextField
                label={f('beds')}
                type="number"
                inputMode="numeric"
                dir="ltr"
                value={String(draft.beds ?? '')}
                onChange={(v) => update({ beds: v })}
              />
            </>
          )}
          {listing.subcategory !== 'eruv' && bilingual('priceNote', 'priceNote')}
          {listing.subcategory === 'eruv' && (
            <>
              {bilingual('statusMessage', 'statusMessage')}
              <p className="text-xs text-ash">{t('directory.admin.details.eruvHint')}</p>
            </>
          )}
        </>
      );
      break;
  }

  if (listing.category === 'shiur') {
    fields = <ShiurDetailsFields listing={listing} draft={draft} update={update} showShulError={shulError} />;
  }

  return (
    <div className="space-y-5">
      <SectionTitle>{t('directory.admin.tab.details')}</SectionTitle>
      {fields}
      <SaveBar dirty={dirty} saving={saving} onSave={handleSave} onCancel={reset} justSaved={justSaved} failed={failed} />
    </div>
  );
}

function BeisHoraahFields({
  listing,
  draft,
  update,
  check,
}: {
  listing: Extract<Listing, { category: 'beisHoraah' }>;
  draft: Draft;
  update: (patch: Draft) => void;
  check: (key: string, labelKey?: string) => React.ReactNode;
}) {
  const { t, i18n } = useTranslation();
  const isHebrew = i18n.language === 'he';
  const f = (key: string) => t(`directory.field.${key}`);
  const isRav = listing.subcategory === 'rav';
  const rabbanim = (draft.rabbanim as RavEntry[] | undefined) ?? [];
  const setRav = (i: number, patch: Partial<RavEntry>) =>
    update({ rabbanim: rabbanim.map((r, j) => (j === i ? { ...r, ...patch } : r)) });

  // A Rav can be connected to a shul or Beis Horaah (plan 6.3).
  const { listings } = useListings({ skip: !isRav });
  const connectable = useMemo(
    () =>
      listings
        .filter((l) => (l.category === 'shul' || l.category === 'beisHoraah') && l.id !== listing.id)
        .sort((a, b) => pickLang(a.nameEn, a.nameHe, isHebrew).localeCompare(pickLang(b.nameEn, b.nameHe, isHebrew))),
    [listings, listing.id, isHebrew]
  );

  return (
    <>
      {!isRav && (
        <fieldset className="space-y-3">
          <legend className="text-sm font-medium text-ink mb-1">{f('rabbanim')}</legend>
          {rabbanim.map((r, i) => (
            <div key={i} className="rounded-[16px] border border-ink/10 p-3 space-y-3">
              <BilingualField
                labelHe={t('directory.admin.details.ravNameHe')}
                labelEn={t('directory.admin.details.ravNameEn')}
                valueHe={r.nameHe ?? ''}
                valueEn={r.nameEn ?? ''}
                onChangeHe={(v) => setRav(i, { nameHe: v })}
                onChangeEn={(v) => setRav(i, { nameEn: v })}
              />
              <BilingualField
                labelHe={t('directory.admin.details.ravRoleHe')}
                labelEn={t('directory.admin.details.ravRoleEn')}
                valueHe={r.roleHe ?? ''}
                valueEn={r.roleEn ?? ''}
                onChangeHe={(v) => setRav(i, { roleHe: v })}
                onChangeEn={(v) => setRav(i, { roleEn: v })}
              />
              <button
                type="button"
                onClick={() => update({ rabbanim: rabbanim.filter((_, j) => j !== i) })}
                className="inline-flex items-center gap-2 min-h-[44px] text-sm text-danger-600 hover:opacity-70"
              >
                <Trash2 className="w-4 h-4" aria-hidden />
                {t('directory.admin.remove')}
              </button>
            </div>
          ))}
          <button
            type="button"
            onClick={() => update({ rabbanim: [...rabbanim, { nameHe: '' }] })}
            className="inline-flex items-center gap-2 min-h-[44px] px-4 rounded-[10px] border border-ink/20 text-sm text-ink hover:border-ink/50"
          >
            <Plus className="w-4 h-4" aria-hidden />
            {t('directory.admin.details.addRav')}
          </button>
        </fieldset>
      )}

      {isRav && (
        <SelectField
          label={t('directory.admin.details.connectedListing')}
          value={(draft.connectedListingId as string) ?? ''}
          onChange={(v) => update({ connectedListingId: v || undefined })}
          options={[
            { value: '', label: t('directory.admin.notSet') },
            ...connectable.map((l) => ({ value: l.id, label: pickLang(l.nameEn, l.nameHe, isHebrew) })),
          ]}
        />
      )}

      <ChipSelect
        label={f('languages')}
        options={LANGUAGE_CODES.map((c) => ({ value: c, label: t(`directory.language.${c}`) }))}
        selected={(draft.languages as string[] | undefined) ?? []}
        onChange={(next) => update({ languages: next })}
      />
      <ChipSelect
        label={f('topics')}
        options={BEIS_HORAAH_TOPICS.map((k) => ({ value: k, label: t(`directory.topic.${k}`) }))}
        selected={(draft.topics as string[] | undefined) ?? []}
        onChange={(next) => update({ topics: next })}
      />
      <TextField
        label={f('shailosLinePhone')}
        type="tel"
        dir="ltr"
        value={(draft.shailosLinePhone as string) ?? ''}
        onChange={(v) => update({ shailosLinePhone: v })}
      />
      {check('walkInAllowed')}
    </>
  );
}
